/**
 * HostnameRouteGate - First-Level Portal Routing Guard
 *
 * Enforces domain boundaries before route rendering:
 * - app.fundelitehq.com = Platform HQ
 * - app.fundelite.ai = Product app
 * - fundelite.ai / www.fundelite.ai = Marketing only
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupportContext } from '../context/SupportContext';
import {
  getHostnamePortal,
  HOSTNAME_PORTALS,
  ADMIN_PORTAL_ROLES,
  CUSTOMER_PORTAL_ROLES,
  AGENCY_ROLES,
  getDashboardForRoleOnPortal,
  getPortalLoginRoute,
  getProductAppUrl,
  getFundGeniusAppUrl,
  isPlatformConsoleRole,
} from '../utils/hostnamePortal';
import {
  isAgencyWorkspaceUser,
  isPlatformStaffUser,
  isClientPortalUser,
  isHostSafeAuthPath,
  resolvePortalBoundaryRedirect,
  isSameNavigationTarget,
} from '../utils/routeResolver';
import {
  getPortalLandingRoute,
  resolveAuthenticatedRootRoute,
} from '../utils/portalLandingRoute';

const GateStatusScreen = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-stone-900">
    <div className="text-center">
      <motionlessSpinner />
      {message ? <p className="text-gray-600 dark:text-gray-400 mt-4">{message}</p> : null}
    </div>
  </div>
);

const motionlessSpinner = () => (
  <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto" />
);

const HostnameRouteGate = ({ children }) => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { isInSupportMode, contextReady } = useSupportContext();
  const [gateStatus, setGateStatus] = useState('checking');
  const [redirectUrl, setRedirectUrl] = useState(null);
  const lastBoundaryRedirectRef = useRef(null);

  const portal = getHostnamePortal();
  const pathname = location.pathname;

  useEffect(() => {
    if (portal === HOSTNAME_PORTALS.DEV) {
      setGateStatus('allowed');
      setRedirectUrl(null);
      return;
    }

    if (loading) {
      setGateStatus('checking');
      return;
    }

    const normalizedRole = user?.role?.toLowerCase() || '';
    const platformConsoleRole = isPlatformConsoleRole(normalizedRole);
    const agencyWorkspaceUser = isAgencyWorkspaceUser(user);

    if (user && platformConsoleRole && !contextReady) {
      setGateStatus('checking');
      return;
    }

    const boundaryRedirect = user
      ? resolvePortalBoundaryRedirect(user, {
          pathname,
          search: location.search || '',
          portal,
          isInSupportMode,
        })
      : null;

    if (boundaryRedirect) {
      if (
        isSameNavigationTarget(boundaryRedirect) ||
        lastBoundaryRedirectRef.current === boundaryRedirect
      ) {
        setGateStatus('allowed');
        setRedirectUrl(null);
        return;
      }
      lastBoundaryRedirectRef.current = boundaryRedirect;
      setRedirectUrl(boundaryRedirect);
      setGateStatus('redirect');
      return;
    }

    lastBoundaryRedirectRef.current = null;

    if (portal === HOSTNAME_PORTALS.MARKETING || portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING) {
      const marketingAllowedRoutes =
        portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING
          ? ['/', '/pricing', '/features']
          : ['/', '/pricing', '/features', '/login', '/signup'];

      const isAllowed = marketingAllowedRoutes.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
      );

      if (!isAllowed) {
        if (portal === HOSTNAME_PORTALS.FUNDGENIUS_MARKETING && pathname.startsWith('/signup')) {
          setRedirectUrl(getFundGeniusAppUrl('/login'));
          setGateStatus('redirect');
          return;
        }
        setRedirectUrl('/');
        setGateStatus('blocked');
        return;
      }

      setGateStatus('allowed');
      setRedirectUrl(null);
      return;
    }

    if (portal === HOSTNAME_PORTALS.FUNDGENIUS_INTAKE) {
      const intakeAllowedRoutes = ['/', '/intake'];
      const isAllowed = intakeAllowedRoutes.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
      );
      if (!isAllowed) {
        setRedirectUrl('/');
        setGateStatus('blocked');
        return;
      }
      setGateStatus('allowed');
      setRedirectUrl(null);
      return;
    }

    if (portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
      if (isHostSafeAuthPath(pathname)) {
        setGateStatus('allowed');
        setRedirectUrl(null);
        return;
      }

      const fundGeniusAllowedRoutes = [
        '/dashboard',
        '/fundgenius-hq',
        '/platform/fundgenius-hq',
        '/signin',
      ];
      const isAllowed = fundGeniusAllowedRoutes.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
      );

      if (!isAllowed) {
        setRedirectUrl(user ? '/platform/fundgenius-hq/clients' : '/login');
        setGateStatus('blocked');
        return;
      }

      if (user && isClientPortalUser(user)) {
        setRedirectUrl(getProductAppUrl('/dashboard'));
        setGateStatus('redirect');
        return;
      }

      setGateStatus('allowed');
      setRedirectUrl(null);
      return;
    }

    if (portal === HOSTNAME_PORTALS.ADMIN) {
      if (isHostSafeAuthPath(pathname)) {
        setGateStatus('allowed');
        setRedirectUrl(null);
        return;
      }

      if (
        pathname.startsWith('/onboarding') ||
        pathname.startsWith('/signup') ||
        pathname.startsWith('/trial')
      ) {
        if (user) {
          if (!ADMIN_PORTAL_ROLES.includes(normalizedRole) || agencyWorkspaceUser) {
            setRedirectUrl(
              agencyWorkspaceUser || AGENCY_ROLES.includes(normalizedRole)
                ? getProductAppUrl('/agency/dashboard')
                : getProductAppUrl('/dashboard')
            );
            setGateStatus('redirect');
            return;
          }

          setRedirectUrl(getDashboardForRoleOnPortal(normalizedRole, portal, user));
          setGateStatus('blocked');
          return;
        }

        setRedirectUrl(getPortalLoginRoute(portal));
        setGateStatus('blocked');
        return;
      }

      if (pathname.startsWith('/agency')) {
        if (platformConsoleRole && isInSupportMode) {
          setGateStatus('allowed');
          setRedirectUrl(null);
          return;
        }

        if (user) {
          if (CUSTOMER_PORTAL_ROLES.includes(normalizedRole) || agencyWorkspaceUser) {
            setRedirectUrl(
              agencyWorkspaceUser || AGENCY_ROLES.includes(normalizedRole)
                ? getProductAppUrl('/agency/dashboard')
                : getProductAppUrl('/dashboard')
            );
            setGateStatus('redirect');
            return;
          }

          setRedirectUrl(getDashboardForRoleOnPortal(normalizedRole, portal, user));
          setGateStatus('blocked');
          return;
        }

        setRedirectUrl(getPortalLoginRoute(portal));
        setGateStatus('blocked');
        return;
      }

      if (
        user &&
        !isPlatformStaffUser(user) &&
        normalizedRole &&
        (!ADMIN_PORTAL_ROLES.includes(normalizedRole) || agencyWorkspaceUser)
      ) {
        if (agencyWorkspaceUser || AGENCY_ROLES.includes(normalizedRole)) {
          setRedirectUrl(getProductAppUrl('/agency/dashboard'));
        } else {
          setRedirectUrl(getProductAppUrl('/dashboard'));
        }
        setGateStatus('redirect');
        return;
      }

      if (pathname === '/' || pathname === '') {
        if (user && ADMIN_PORTAL_ROLES.includes(normalizedRole) && !agencyWorkspaceUser) {
          // Root is authoritative: platform owner/admin → /platform/dashboard (never Agencies).
          setRedirectUrl(
            resolveAuthenticatedRootRoute({
              hostname: typeof window !== 'undefined' ? window.location.hostname : '',
              user,
            })
            || getPortalLandingRoute({
              hostname: typeof window !== 'undefined' ? window.location.hostname : '',
              user,
            })
            || getDashboardForRoleOnPortal(normalizedRole, portal, user)
          );
          setGateStatus('blocked');
          return;
        }

        if (!user) {
          setRedirectUrl('/login');
          setGateStatus('blocked');
          return;
        }
      }
    }

    if (portal === HOSTNAME_PORTALS.CUSTOMER) {
      if (isHostSafeAuthPath(pathname)) {
        setGateStatus('allowed');
        setRedirectUrl(null);
        return;
      }

      if (pathname.startsWith('/staff') || pathname.startsWith('/platform')) {
        if (user && isPlatformStaffUser(user) && !isInSupportMode) {
          setGateStatus('allowed');
          setRedirectUrl(null);
          return;
        }

        if (user) {
          setRedirectUrl(
            agencyWorkspaceUser || AGENCY_ROLES.includes(normalizedRole)
              ? '/agency/dashboard'
              : '/dashboard'
          );
        } else {
          setRedirectUrl('/login');
        }
        setGateStatus('blocked');
        return;
      }
    }

    setGateStatus('allowed');
    setRedirectUrl(null);
  }, [portal, pathname, location.search, user, loading, isInSupportMode, contextReady]);

  if (gateStatus === 'checking' || loading) {
    return <GateStatusScreen />;
  }

  if (gateStatus === 'redirect' && redirectUrl?.startsWith('http')) {
    // Preview absolute URLs are same-origin (getProductAppUrl stays local) —
    // use React Router, never jump to production.
    try {
      const targetOrigin = new URL(redirectUrl).origin;
      if (
        typeof window !== 'undefined'
        && targetOrigin === window.location.origin
      ) {
        const path = `${new URL(redirectUrl).pathname}${new URL(redirectUrl).search}${new URL(redirectUrl).hash}`;
        return <Navigate to={path || '/'} replace />;
      }
    } catch {
      /* fall through */
    }
    if (!isSameNavigationTarget(redirectUrl)) {
      window.location.replace(redirectUrl);
    }
    return <GateStatusScreen message="Redirecting to correct portal..." />;
  }

  if (gateStatus === 'blocked' && redirectUrl) {
    return <Navigate to={redirectUrl} replace />;
  }

  return children;
};

export default HostnameRouteGate;
